import { useContext, useEffect, useState } from "react";
import ToastContext from "../../../context/toastProvider";
import usePricingEditor, { computeConfigDiff } from "../../../components/pricing/usePricingEditor";
import { describePath, formatDiffValue } from "../../../components/pricing/pricingDiffFormat";
import {
  FEE_FIELD_LABELS,
  PRIORITY_FIELD_LABELS,
  PRIORITY_KEYS,
  PRIORITY_LABELS,
  PRODUCT_KEYS,
  PRODUCT_LABELS,
} from "../../../components/pricing/pricingMeta";
import { formatMoneyInput, handleMoneyInputChange } from "../../../components/productPackages/moneyInput";
import { cashnator } from "../../../components/utils/functions";
import MobileSection from "../../ui/MobileSection";
import MobileFormField from "../../ui/MobileFormField";
import MobileStickyActions from "../../ui/MobileStickyActions";
import MobileConfirm from "../../ui/MobileConfirm";
import MobileSkeleton from "../../ui/MobileSkeleton";
import MobileError from "../../ui/MobileError";
import { mobileInput, mobilePrimaryBtn, mobileSecondaryBtn } from "../../theme";

const PRIORITY_FIELD_ORDER = [
  "additionalRate",
  "daysPerItem",
  "discountPerItem",
  "maxDiscount",
  "minDeadlineDays",
  "maxItems",
  "hubItemsPerCase",
];
const PERCENT_PRIORITY_FIELDS = ["additionalRate", "discountPerItem", "maxDiscount"];
const INTEGER_PRIORITY_FIELDS = ["minDeadlineDays", "maxItems", "hubItemsPerCase"];

const toPercentDisplay = (rate) => Number(((Number(rate) || 0) * 100).toFixed(4));
const fromPercentInput = (raw) => Number(((Number(raw) || 0) / 100).toFixed(6));

const formatDate = (value) => {
  if (!value) {
    return "-";
  }
  return new Intl.DateTimeFormat("pt-BR").format(new Date(value));
};

// describePath/formatDiffValue vêm de pricingDiffFormat (compartilhados com o desktop).

function MobileProductsSection({ draft, setProductField, toggleProductActive }) {
  return (
    <MobileSection title="Produtos">
      {PRODUCT_KEYS.map((key) => {
        const product = draft.products?.[key] || {};
        const label = PRODUCT_LABELS[key] || key;
        return (
          <div key={key} className="mb-4 rounded-lg border border-primary/30 bg-monao p-3">
            <div className="mb-2 flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-laranjinha">{label}</p>
              <label className="flex min-h-[44px] items-center gap-2 text-xs text-laranjinha/80">
                <input
                  type="checkbox"
                  aria-label={`Ativo: ${label}`}
                  checked={Boolean(product.active)}
                  onChange={() => toggleProductActive(key)}
                  className="h-5 w-5 rounded border-primary/50 bg-monao accent-primary"
                />
                Ativo
              </label>
            </div>
            <MobileFormField label="Preço base">
              <input
                type="text"
                inputMode="numeric"
                aria-label={`Preço base: ${label}`}
                value={formatMoneyInput(product.baseCents)}
                onChange={(event) => handleMoneyInputChange(
                  event.target.value,
                  (cents) => setProductField(key, "baseCents", cents)
                )}
                className={mobileInput}
              />
            </MobileFormField>
            {key === "pre" ? (
              <>
                <MobileFormField label="Preço do lacre com subgrades">
                  <input
                    type="text"
                    inputMode="numeric"
                    aria-label="Preço do lacre com subgrades"
                    value={formatMoneyInput(product.preLacre?.withSubgradesCents)}
                    onChange={(event) => handleMoneyInputChange(
                      event.target.value,
                      (cents) => setProductField(key, "preLacre.withSubgradesCents", cents)
                    )}
                    className={mobileInput}
                  />
                </MobileFormField>
                <MobileFormField label="Preço do lacre sem subgrades">
                  <input
                    type="text"
                    inputMode="numeric"
                    aria-label="Preço do lacre sem subgrades"
                    value={formatMoneyInput(product.preLacre?.withoutSubgradesCents)}
                    onChange={(event) => handleMoneyInputChange(
                      event.target.value,
                      (cents) => setProductField(key, "preLacre.withoutSubgradesCents", cents)
                    )}
                    className={mobileInput}
                  />
                </MobileFormField>
              </>
            ) : null}
          </div>
        );
      })}
    </MobileSection>
  );
}

function MobilePrioritiesSection({ draft, setPriorityField, togglePriorityActive }) {
  return (
    <MobileSection title="Prioridades">
      {PRIORITY_KEYS.map((key) => {
        const priority = draft.priorities?.[key] || {};
        const label = PRIORITY_LABELS[key] || key;
        return (
          <div key={key} className="mb-4 rounded-lg border border-primary/30 bg-monao p-3">
            <div className="mb-2 flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-laranjinha">{label}</p>
              <label className="flex min-h-[44px] items-center gap-2 text-xs text-laranjinha/80">
                <input
                  type="checkbox"
                  aria-label={`Ativo: ${label}`}
                  checked={Boolean(priority.active)}
                  onChange={() => togglePriorityActive(key)}
                  className="h-5 w-5 rounded border-primary/50 bg-monao accent-primary"
                />
                Ativo
              </label>
            </div>
            {PRIORITY_FIELD_ORDER.map((field) => {
              const isPercent = PERCENT_PRIORITY_FIELDS.includes(field);
              const isInteger = INTEGER_PRIORITY_FIELDS.includes(field);
              const value = isPercent ? toPercentDisplay(priority[field]) : priority[field] ?? 0;
              const fieldLabel = PRIORITY_FIELD_LABELS[field] + (isPercent ? " (%)" : "");
              return (
                <MobileFormField key={field} label={fieldLabel}>
                  <input
                    type="number"
                    step={isInteger ? "1" : "any"}
                    aria-label={`${PRIORITY_FIELD_LABELS[field]}: ${label}`}
                    value={value}
                    onChange={(event) => {
                      if (isPercent) {
                        setPriorityField(key, field, fromPercentInput(event.target.value));
                        return;
                      }
                      const parsed = isInteger
                        ? parseInt(event.target.value, 10)
                        : parseFloat(event.target.value);
                      setPriorityField(key, field, Number.isNaN(parsed) ? 0 : parsed);
                    }}
                    className={mobileInput}
                  />
                </MobileFormField>
              );
            })}
          </div>
        );
      })}
    </MobileSection>
  );
}

function MobileFeesSection({ draft, setFeeField }) {
  return (
    <MobileSection title="Taxas">
      <MobileFormField label={`${FEE_FIELD_LABELS.handlingFeeRate} (%)`}>
        <input
          type="number"
          step="any"
          aria-label={FEE_FIELD_LABELS.handlingFeeRate}
          value={toPercentDisplay(draft.fees?.handlingFeeRate)}
          onChange={(event) => setFeeField("handlingFeeRate", fromPercentInput(event.target.value))}
          className={mobileInput}
        />
      </MobileFormField>
      <MobileFormField label={FEE_FIELD_LABELS.autographCents}>
        <input
          type="text"
          inputMode="numeric"
          aria-label={FEE_FIELD_LABELS.autographCents}
          value={formatMoneyInput(draft.fees?.autographCents)}
          onChange={(event) => handleMoneyInputChange(
            event.target.value,
            (cents) => setFeeField("autographCents", cents)
          )}
          className={mobileInput}
        />
      </MobileFormField>
    </MobileSection>
  );
}

function MobileSimulatorSection({ draft, simulation, runSimulation }) {
  const activeProductKeys = PRODUCT_KEYS.filter((key) => draft?.products?.[key]?.active);
  const activePriorityKeys = PRIORITY_KEYS.filter((key) => draft?.priorities?.[key]?.active);

  const [gradingType, setGradingType] = useState(activeProductKeys[0] || "");
  const [numberOfCards, setNumberOfCards] = useState(10);
  const [autographEvaluation, setAutographEvaluation] = useState(false);

  useEffect(() => {
    if (!activeProductKeys.includes(gradingType)) {
      setGradingType(activeProductKeys[0] || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProductKeys.join(",")]);

  useEffect(() => {
    if (!gradingType || !numberOfCards || Number(numberOfCards) < 1 || activePriorityKeys.length === 0) {
      return;
    }
    runSimulation({
      cases: activePriorityKeys.map((priority) => ({
        gradingType,
        numberOfCards: Number(numberOfCards),
        priority,
        autographEvaluation,
      })),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gradingType, numberOfCards, autographEvaluation, activePriorityKeys.join(","), runSimulation]);

  const results = simulation?.status === "ready" ? simulation?.result?.results || [] : [];
  const baseSubtotal = results[0]?.subtotalCents;
  const formatDiscount = (row) => {
    if (!row.rawPriceCents || row.rawPriceCents <= row.subtotalCents) {
      return "0%";
    }
    const percent = Math.round(((row.rawPriceCents - row.subtotalCents) / row.rawPriceCents) * 1000) / 10;
    return `${String(percent).replace(".", ",")}%`;
  };
  const formatDelta = (subtotalCents) => {
    if (!baseSubtotal || subtotalCents === baseSubtotal) {
      return "base";
    }
    const delta = Math.round(((subtotalCents - baseSubtotal) / baseSubtotal) * 1000) / 10;
    return `${delta > 0 ? "+" : ""}${String(delta).replace(".", ",")}% vs. base`;
  };

  return (
    <details className="border-b border-primary/30 bg-mgs-bg px-4 py-4">
      <summary className="cursor-pointer text-sm font-semibold uppercase tracking-wide text-primary">
        Simulador
      </summary>
      <div className="mt-3">
        <MobileFormField label="Produto">
          <select
            aria-label="Produto"
            value={gradingType}
            onChange={(event) => setGradingType(event.target.value)}
            className={mobileInput}
          >
            {activeProductKeys.length === 0 ? <option value="">Nenhum produto ativo</option> : null}
            {activeProductKeys.map((key) => (
              <option key={key} value={key}>{PRODUCT_LABELS[key] || key}</option>
            ))}
          </select>
        </MobileFormField>
        <MobileFormField label="Quantidade de cartas">
          <input
            type="number"
            min="1"
            aria-label="Quantidade de cartas"
            value={numberOfCards}
            onChange={(event) => setNumberOfCards(event.target.value)}
            className={mobileInput}
          />
        </MobileFormField>
        <label className="flex min-h-[44px] items-center gap-2 text-sm text-laranjinha/80">
          <input
            type="checkbox"
            aria-label="Autógrafo"
            checked={autographEvaluation}
            onChange={(event) => setAutographEvaluation(event.target.checked)}
            className="h-5 w-5 rounded border-primary/50 bg-monao accent-primary"
          />
          Autógrafo
        </label>
        <div className="mt-3 rounded-lg border border-dashed border-primary/40 p-3 text-sm text-laranjinha">
          {simulation?.status === "loading" ? <p>Calculando...</p> : null}
          {simulation?.status === "error" ? (
            <p className="text-red-300">{simulation?.result?.message || "Erro na simulação."}</p>
          ) : null}
          {simulation?.status === "ready" && results.length > 0 ? (
            <ul className="divide-y divide-primary/10">
              {results.map((row, index) => (
                <li key={row.priority} className={`py-2 ${index === 0 ? "rounded bg-primary/10 px-2" : ""}`} data-testid={`sim-row-${row.priority}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold">{PRIORITY_LABELS[row.priority] || row.priority}</span>
                    <strong>{cashnator(row.subtotalCents)}</strong>
                  </div>
                  <div className="flex items-center justify-between gap-2 text-xs text-laranjinha/70">
                    <span>{row.deadlineDays} dias úteis · desconto {formatDiscount(row)}</span>
                    <span>{formatDelta(row.subtotalCents)}</span>
                  </div>
                </li>
              ))}
            </ul>
          ) : null}
          {simulation?.status === "idle" ? (
            <p className="text-laranjinha/60">Ajuste os campos para simular.</p>
          ) : null}
        </div>
      </div>
    </details>
  );
}

function MobileHistorySection({ versions, activeVersion, activateVersion }) {
  const { goodToast, badToast } = useContext(ToastContext);
  const [expandedVersion, setExpandedVersion] = useState(null);

  const activeConfig = versions.find((entry) => entry.version === activeVersion)?.config;

  const handleReactivate = async (version) => {
    if (!window.confirm(`Reativar a versão ${version}? As mudanças listadas passam a valer em até 30s.`)) {
      return;
    }
    const response = await activateVersion(version);
    if (response?.type === "success") {
      goodToast(`Versão ${version} reativada.`);
      setExpandedVersion(null);
    } else {
      badToast(response?.message || "Erro ao reativar versão.");
    }
  };

  return (
    <details className="border-b border-primary/30 bg-mgs-bg px-4 py-4">
      <summary className="cursor-pointer text-sm font-semibold uppercase tracking-wide text-primary">
        Histórico
      </summary>
      <div className="mt-3 space-y-2">
        {versions.length === 0 ? (
          <p className="text-sm text-laranjinha/60">Nenhuma versão encontrada.</p>
        ) : (
          versions.map((entry) => {
            const isActive = entry.version === activeVersion;
            return (
              <div
                key={entry.version}
                className="rounded-lg border border-primary/30 bg-monao p-3 text-sm text-laranjinha"
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="font-semibold">Versão {entry.version}</p>
                  {isActive ? (
                    <span className="rounded bg-primary px-2 py-1 text-xs font-semibold text-mgs-bg">
                      ativa
                    </span>
                  ) : null}
                </div>
                <p className="mt-1 text-xs text-laranjinha/70">{formatDate(entry.createdAt)} · {entry.createdBy || "-"}</p>
                {entry.note ? <p className="mt-1 text-xs text-laranjinha/70">{entry.note}</p> : null}
                {!isActive ? (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setExpandedVersion(expandedVersion === entry.version ? null : entry.version)}
                      className={mobileSecondaryBtn}
                    >
                      {expandedVersion === entry.version ? "ocultar" : "ver mudanças"}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleReactivate(entry.version)}
                      className={mobileSecondaryBtn}
                    >
                      Reativar
                    </button>
                  </div>
                ) : null}
                {expandedVersion === entry.version ? (
                  <div className="mt-2 rounded border border-primary/20 bg-black/30 p-2" data-testid={`history-diff-${entry.version}`}>
                    {(() => {
                      const diffEntries = activeConfig && entry.config
                        ? computeConfigDiff(activeConfig, entry.config)
                        : [];
                      if (diffEntries.length === 0) {
                        return (
                          <p className="text-xs text-laranjinha/70">
                            Configuração idêntica à versão ativa — reativar não muda nenhum valor.
                          </p>
                        );
                      }
                      return (
                        <div>
                          <p className="mb-1 text-xs font-semibold text-primary">
                            Reativar aplicará estas mudanças:
                          </p>
                          <ul className="space-y-1 text-xs text-laranjinha">
                            {diffEntries.map((change) => (
                              <li key={change.path}>
                                <span className="block text-laranjinha/70">{describePath(change.path)}</span>
                                <span className="line-through opacity-60">{formatDiffValue(change.path, change.before)}</span>
                                {" → "}
                                <span className="font-semibold">{formatDiffValue(change.path, change.after)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })()}
                  </div>
                ) : null}
              </div>
            );
          })
        )}
      </div>
    </details>
  );
}

export default function MobilePricing({ user }) {
  const { badToast, goodToast } = useContext(ToastContext);
  const token = user?.token;
  const editor = usePricingEditor({ token });
  const {
    status,
    draft,
    activeVersion,
    versions,
    diff,
    isDirty,
    validationErrors,
    simulation,
    setProductField,
    setPriorityField,
    setFeeField,
    toggleProductActive,
    togglePriorityActive,
    runSimulation,
    publish,
    activateVersion,
    reload,
  } = editor;

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (status === "error") {
      badToast("Não foi possível carregar a configuração de preços.");
    }
  }, [status, badToast]);

  if (status === "loading") {
    return (
      <div aria-label="Carregando precificação" role="status">
        <MobileSkeleton lines={6} />
      </div>
    );
  }

  if (status === "error") {
    return <MobileError message="Não foi possível carregar a configuração de preços." onRetry={reload} />;
  }

  if (!draft) {
    return null;
  }

  const publishDisabled = !isDirty || validationErrors.length > 0;

  const handlePublish = async () => {
    setSubmitting(true);
    const response = await publish(note);
    setSubmitting(false);
    if (response?.type === "success") {
      goodToast("Nova versão publicada — vale em até 30s");
      setConfirmOpen(false);
      setNote("");
    } else {
      badToast(response?.message || "Erro ao publicar configuração.");
    }
  };

  const confirmMessage = (
    <div className="text-left">
      {diff.length > 0 ? (
        <ul className="mb-3 max-h-48 space-y-1 overflow-y-auto text-xs text-laranjinha/80">
          {diff.map((entry) => (
            <li key={entry.path}>
              {describePath(entry.path)}: {formatDiffValue(entry.path, entry.before)} → {formatDiffValue(entry.path, entry.after)}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-3 text-xs text-laranjinha/60">Nenhuma alteração pendente.</p>
      )}
      <label className="block text-sm font-medium text-laranjinha">
        Nota (opcional)
        <textarea
          rows={3}
          aria-label="Nota"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Ex.: reajuste de tabela de agosto"
          className={`mt-1 ${mobileInput}`}
        />
      </label>
    </div>
  );

  return (
    <div className="flex flex-col pb-4">
      <MobileProductsSection draft={draft} setProductField={setProductField} toggleProductActive={toggleProductActive} />
      <MobilePrioritiesSection draft={draft} setPriorityField={setPriorityField} togglePriorityActive={togglePriorityActive} />
      <MobileFeesSection draft={draft} setFeeField={setFeeField} />

      {validationErrors.length > 0 ? (
        <ul className="mx-4 mb-4 rounded-lg border border-red-500 bg-red-950/40 p-3 text-sm text-red-300">
          {validationErrors.map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      ) : null}

      <MobileSimulatorSection draft={draft} simulation={simulation} runSimulation={runSimulation} />
      <MobileHistorySection versions={versions} activeVersion={activeVersion} activateVersion={activateVersion} />

      <MobileStickyActions>
        <button
          type="button"
          disabled={publishDisabled}
          onClick={() => setConfirmOpen(true)}
          className={`flex-1 ${mobilePrimaryBtn}`}
        >
          Publicar
        </button>
      </MobileStickyActions>

      <MobileConfirm
        open={confirmOpen}
        title="Confirmar publicação"
        message={confirmMessage}
        confirmLabel={submitting ? "Publicando..." : "Confirmar"}
        onConfirm={submitting ? () => {} : handlePublish}
        onCancel={submitting ? () => {} : () => setConfirmOpen(false)}
      />
    </div>
  );
}
